/* ==========================================================================
   Signature Capture Component
   Canvas-based electronic signature pad for provider prescription signing
   ========================================================================== */

import { useRef, useState, useEffect, useCallback } from 'react'
import { Check } from 'lucide-react'

interface SignatureCaptureProps {
  /** Called with base64 PNG data URL when signature changes, or null when cleared */
  onChange: (signature: string | null) => void
  /** Whether the field is required */
  required?: boolean
  /** Whether the field is disabled */
  disabled?: boolean
  /** Error message to display */
  error?: string
  /** Pre-existing signature (base64 PNG data URL) from a restored draft. When set,
   *  the component shows a "Signature on file" state instead of re-painting the
   *  canvas; choosing "Replace signature" clears it and reveals a blank canvas.
   *  Must be a stable mount-time value (not live signature state). */
  initialValue?: string | null
}

export function SignatureCapture({ onChange, required, disabled, error, initialValue }: SignatureCaptureProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [hasSignature, setHasSignature] = useState(false)
  const lastPos = useRef<{ x: number; y: number } | null>(null)

  // A restored draft signature shows a "Signature on file" panel rather than being
  // painted onto the canvas. "Replace signature" sets this and reveals the canvas.
  const [resigning, setResigning] = useState(false)
  const showSignedPanel = !!initialValue && !resigning

  // Set up canvas dimensions, DPR scaling, and drawing properties
  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const rect = canvas.getBoundingClientRect()
    canvas.width = rect.width * window.devicePixelRatio
    canvas.height = rect.height * window.devicePixelRatio
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio)
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, rect.width, rect.height)
    ctx.strokeStyle = '#1a1a2e'
    ctx.lineWidth = 2
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
  }, [])

  // Initialize the canvas whenever it is the visible view. It is conditionally
  // rendered (hidden while the "Signature on file" panel shows), so this must
  // re-run when switching to the canvas — not only on first mount — otherwise
  // getBoundingClientRect would size a not-yet-laid-out canvas.
  useEffect(() => {
    if (!showSignedPanel) initCanvas()
  }, [showSignedPanel, initCanvas])

  // Replace a restored signature: discard it and reveal a blank canvas. With the
  // signature cleared, the parent's required-signature gate (when signing is
  // required) blocks submit until the provider signs again.
  const handleReplace = () => {
    onChange(null)
    setHasSignature(false)
    setResigning(true)
  }

  const getPos = (canvas: HTMLCanvasElement, e: MouseEvent | TouchEvent) => {
    const rect = canvas.getBoundingClientRect()
    if ('touches' in e) {
      const touch = e.touches[0]
      return {
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top,
      }
    }
    return {
      x: (e as MouseEvent).clientX - rect.left,
      y: (e as MouseEvent).clientY - rect.top,
    }
  }

  const startDrawing = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (disabled) return
    e.preventDefault()
    const canvas = canvasRef.current
    if (!canvas) return
    setIsDrawing(true)
    const nativeEvent = e.nativeEvent as MouseEvent | TouchEvent
    lastPos.current = getPos(canvas, nativeEvent)
  }, [disabled])

  const draw = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing || disabled) return
    e.preventDefault()
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const nativeEvent = e.nativeEvent as MouseEvent | TouchEvent
    const pos = getPos(canvas, nativeEvent)

    if (lastPos.current) {
      ctx.beginPath()
      ctx.moveTo(lastPos.current.x, lastPos.current.y)
      ctx.lineTo(pos.x, pos.y)
      ctx.stroke()
    }
    lastPos.current = pos

    if (!hasSignature) {
      setHasSignature(true)
    }
  }, [isDrawing, disabled, hasSignature])

  const stopDrawing = useCallback(() => {
    if (!isDrawing) return
    setIsDrawing(false)
    lastPos.current = null

    const canvas = canvasRef.current
    if (!canvas || !hasSignature) return

    const dataUrl = canvas.toDataURL('image/png')
    onChange(dataUrl)
  }, [isDrawing, hasSignature, onChange])

  const handleClear = () => {
    const canvas = canvasRef.current
    if (!canvas) return

    // Reset canvas dimensions to fully clear all pixel data and context state
    canvas.width = canvas.width
    initCanvas()

    lastPos.current = null
    setHasSignature(false)
    onChange(null)
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-charcoal">
          Provider Signature
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        {!showSignedPanel && hasSignature && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="text-xs text-gray-500 hover:text-gray-700 underline"
          >
            Clear
          </button>
        )}
      </div>

      {showSignedPanel ? (
        <div className="flex items-center justify-between border-2 border-gray-300 rounded-lg p-4 bg-gray-50">
          <span className="flex items-center gap-2 text-sm font-medium text-charcoal">
            <Check className="h-4 w-4 text-green-600" aria-hidden="true" />
            Signature on file
          </span>
          {!disabled && (
            <button
              type="button"
              onClick={handleReplace}
              className="text-xs text-gray-500 hover:text-gray-700 underline"
            >
              Replace signature
            </button>
          )}
        </div>
      ) : (
        <div
          className={`
            relative border-2 rounded-lg overflow-hidden
            ${error ? 'border-red-500' : 'border-gray-300'}
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-crosshair'}
          `}
        >
          <canvas
            ref={canvasRef}
            className="w-full block touch-none"
            style={{ height: '120px' }}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
          />
          {!hasSignature && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <p className="text-sm text-gray-400 select-none">Sign here</p>
            </div>
          )}
        </div>
      )}

      {error ? (
        <p className="text-xs text-red-600">{error}</p>
      ) : (
        <p className="text-xs text-gray-500">
          {showSignedPanel
            ? 'You signed this saved script. Choose "Replace signature" to sign again.'
            : 'Draw your signature above using your mouse or touch screen.'}
        </p>
      )}
    </div>
  )
}

export default SignatureCapture
